import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { FileUsageSource, FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import numeral from 'numeral'
import { useMemo } from 'react'

import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import Icon from 'components/uiKit/Icon'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { useFileByUsage } from 'utils/files'
import { testProps } from 'utils/test/qaData'

import * as s from './DownloaderElement.module.scss'
import type { DownloaderElementType } from './downloaderTypes'
import DownloaderStyled from './styled/DownloaderStyled'

const maxLengthLabel = 150

const DownloaderElement: DownloaderElementType = ({ element, styles, mode, font, block }) => {
  const { file, label } = element.value

  const fileData = useFileByUsage(file, FileUsageTypeEnum.common)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.downloader.type,
      elementId: element.id,
    },
  )

  const size = useMemo(
    () => (fileData?.file?.size && numeral(fileData?.file?.size).format('0.0 b')) || '0.0 MB',
    [fileData?.file?.size],
  )

  const isTypeFile = file?.source === FileUsageSource.file ? fileData?.file?.ext : false

  const disabled = mode.previewMode === PreviewMode.editor || mode.editorMode === AppMode.comment

  const labelValue = label && label?.length >= maxLengthLabel ? `${label}...` : label
  const ariaLabel = 'Downloadable file'

  const elementSrOnlyLabelId = `downloader-element-${element.id}`
  const fileTypeId = `downloader-file-type-${element.id}`
  const fileNameId = `downloader-file-name-${element.id}`
  const fileSizeId = `downloader-file-size-${element.id}`

  const ariaLabelledby = [elementSrOnlyLabelId, fileNameId, fileTypeId, fileSizeId]
    .filter(Boolean)
    .join(' ')

  // TODO: handle download interacted
  const onClick = () => {
    if (!fileData && file?.source === FileUsageSource.url) {
      return
    }

    if (!disabled) {
      const data = entityAnalyticsConfig?.interacted({
        resultExtensions: { fileId: file?.id, fileName: fileData?.name, fileUrl: fileData?.path },
        objectExtensions: baseObjectExtensions,
        elementId: block?.uuid || '',
      })
      sendXapiData(data)
      // saveAs(fileData?.path || '', `${fileData?.name}`)
    }
  }

  // todo change icon
  const buttonContent = (
    <>
      <ScreenReaderOnlyText id={elementSrOnlyLabelId}>Downloadable content.</ScreenReaderOnlyText>
      <div className={s.icon}>
        <Icon name='otherFile' style={styles.icon} aria-hidden />
        {isTypeFile && (
          <div className={s.iconType} style={styles.label}>
            <span
              aria-label={`File type: .${fileData?.file?.ext}.`}
              id={fileTypeId}
              style={styles.label}
            >
              .{fileData?.file?.ext}
            </span>
          </div>
        )}
      </div>
      <span
        className={s.text}
        {...testProps({
          el: 'downloaderElLink',
          name: file,
          type: fileData?.file?.ext,
        })}
        aria-label={`Name: ${labelValue || 'Empty'}.`}
        id={fileNameId}
      >
        {labelValue || 'Empty'}
      </span>
      {file?.source === FileUsageSource.file && (
        <div aria-label={`File size: ${size}.`} className={s.size} id={fileSizeId}>
          {size}
        </div>
      )}
    </>
  )

  return (
    <DownloaderStyled
      $fonts={font}
      $styles={styles}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      as='a'
      download={fileData?.name}
      href={(!disabled && fileData?.path) || undefined}
      onClick={onClick}
      rel='noreferrer'
      target='_blank'
    >
      {buttonContent}
    </DownloaderStyled>
  )
}

export default DownloaderElement
